import { Link, useParams } from 'react-router-dom';
import styled from 'styled-components';

import { AuditLogEntryFragmentFragment, useGetAuditLogQuery } from 'data/__generated__';

import { getExtendedFormattedDatetime } from 'helpers/dateTimes';

import { CopyValue } from 'components/CopyValue';
import { Header, HeaderTitle } from 'components/Header';
import { Loader } from 'components/Loader';
import { PageTitle } from 'components/PageTitle';
import { PAGES } from 'constants/pages';

import { AuditChangeList } from 'components/AuditLogs/AuditChangeList';
import { getAuditDisplay } from 'components/AuditLogs/audit-display.config';
import { formatRequestName, getCreatorName, OPERATION_LABELS } from 'components/AuditLogs';

/** Les patches d'une ligne, dans la forme qu'attend `AuditChangeList`. */
const toChanges = ({
    _id,
    collectionName,
    patches,
}: Pick<AuditLogEntryFragmentFragment, '_id' | 'collectionName' | 'patches'>) =>
    patches.map((patch, index) => ({ key: `${_id}-${index}`, collectionName, patch }));

export const AuditDetails = () => {
    const { auditLogId = '' } = useParams<{ auditLogId: string }>();
    const { data, loading, error } = useGetAuditLogQuery({ variables: { auditLogId } });

    if (loading) {
        return <Loader />;
    }

    if (error || !data) {
        return <Message>Impossible de charger cette entrée d’audit.</Message>;
    }

    const { auditLog } = data;
    const detailPath = getAuditDisplay(auditLog.collectionName)?.detailPath;

    return (
        <Container>
            <Header>
                <HeaderTitle>
                    <PageTitle page={PAGES.auditDetails} />
                </HeaderTitle>
            </Header>
            <Content>
                <Card>
                    <Fields>
                        <Field>
                            <FieldLabel>Date</FieldLabel>
                            <FieldValue>{getExtendedFormattedDatetime(auditLog.createdAt)}</FieldValue>
                        </Field>
                        <Field>
                            <FieldLabel>Auteur</FieldLabel>
                            <FieldValue>{getCreatorName(auditLog.createdBy, auditLog.createdByName)}</FieldValue>
                        </Field>
                        <Field>
                            <FieldLabel>Collection</FieldLabel>
                            <FieldValue>{auditLog.collectionName}</FieldValue>
                        </Field>
                        <Field>
                            <FieldLabel>Opération</FieldLabel>
                            <FieldValue>{OPERATION_LABELS[auditLog.operation]}</FieldValue>
                        </Field>
                        <Field>
                            <FieldLabel>Id du document</FieldLabel>
                            <FieldValue>
                                {detailPath ? (
                                    <Link to={`${detailPath}${auditLog.collectionDocumentId}`}>
                                        {auditLog.collectionDocumentId}
                                    </Link>
                                ) : (
                                    <CopyValue value={auditLog.collectionDocumentId} />
                                )}
                            </FieldValue>
                        </Field>
                        <Field>
                            <FieldLabel>Action</FieldLabel>
                            <FieldValue>{formatRequestName(auditLog.requestName) ?? '—'}</FieldValue>
                        </Field>
                        <Field>
                            <FieldLabel>Identifiant d’action</FieldLabel>
                            <FieldValue>
                                {auditLog.requestId ? (
                                    // Toutes les écritures d'un même clic : c'est le regroupement causal.
                                    <Link to={`${PAGES.audit.url}?requestId=${auditLog.requestId}`}>
                                        {auditLog.requestId}
                                    </Link>
                                ) : (
                                    '—'
                                )}
                            </FieldValue>
                        </Field>
                    </Fields>
                </Card>

                <Card>
                    <CardTitle>
                        {auditLog.patches.length} changement{auditLog.patches.length > 1 ? 's' : ''}
                    </CardTitle>
                    {auditLog.patches.length ? (
                        <AuditChangeList changes={toChanges(auditLog)} />
                    ) : (
                        <Message>Aucun changement détaillé sur cette entrée.</Message>
                    )}
                </Card>

                {/* Document principal ci-dessus, documents secondaires ici : ce que le MÊME clic a
                    écrit ailleurs. Le serveur le porte sur la ligne, il n'y a rien à recoudre. */}
                {auditLog.sameRequestAuditLogs.length ? (
                    <Card>
                        <CardTitle>
                            {auditLog.sameRequestAuditLogs.length} autre
                            {auditLog.sameRequestAuditLogs.length > 1
                                ? 's documents modifiés'
                                : ' document modifié'}{' '}
                            par la même action
                        </CardTitle>
                        <RelatedList>
                            {auditLog.sameRequestAuditLogs.map((relatedAuditLog) => (
                                <Related key={relatedAuditLog._id}>
                                    <RelatedHeader>
                                        <Link to={`${PAGES.audit.url}/${relatedAuditLog._id}`}>
                                            {relatedAuditLog.collectionName}
                                        </Link>
                                        <RelatedOperation>
                                            {OPERATION_LABELS[relatedAuditLog.operation]} ·{' '}
                                            {relatedAuditLog.collectionDocumentId}
                                        </RelatedOperation>
                                    </RelatedHeader>
                                    {relatedAuditLog.patches.length ? (
                                        <AuditChangeList changes={toChanges(relatedAuditLog)} />
                                    ) : null}
                                </Related>
                            ))}
                        </RelatedList>
                    </Card>
                ) : null}
            </Content>
        </Container>
    );
};

const Container = styled.div`
    display: flex;
    flex-direction: column;
    flex: 1;
    height: 100%;
    background-color: ${({ theme }) => theme.backgroundColor};
    color: ${({ theme }) => theme.textColor};
`;

const Content = styled.div`
    display: flex;
    flex-direction: column;
    gap: 15px;
    padding: 15px;
    flex: 1;
    overflow-y: auto;
`;

const Card = styled.div`
    // Même fond que les sections d'une page de détail : sans lui la carte reprend le gris de la page.
    background-color: ${({ theme }) => theme.cardBackgroundColor};
    border: 1px solid ${({ theme }) => theme.lightBorderColor};
    border-radius: var(--radius-lg);
    padding: 15px;
`;

const CardTitle = styled.h3`
    margin: 0 0 12px;
    font-size: 14px;
`;

const Fields = styled.div`
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
    gap: 12px;
`;

const Field = styled.div`
    display: flex;
    flex-direction: column;
    gap: 2px;
`;

const FieldLabel = styled.span`
    font-size: 12px;
    font-weight: 600;
    color: ${({ theme }) => theme.ctaPrimaryTextColor};
`;

const FieldValue = styled.span`
    font-size: 14px;
    word-break: break-word;
`;

const RelatedList = styled.div`
    display: flex;
    flex-direction: column;
    gap: 12px;
`;

const Related = styled.div`
    display: flex;
    flex-direction: column;
    gap: 6px;
`;

const RelatedHeader = styled.div`
    display: flex;
    align-items: baseline;
    gap: 8px;
    font-size: 13px;
`;

const RelatedOperation = styled.span`
    font-size: 12px;
    color: ${({ theme }) => theme.ctaPrimaryTextColor};
`;

const Message = styled.div`
    color: ${({ theme }) => theme.textColor};
    opacity: 0.6;
    font-size: 13px;
    padding: 8px 0;
`;
